<script setup>
import { ref } from "vue";
import ListPagination from "./ListPagination.vue";
import JourneyDepth from "./JourneyDepth.vue";
import fetchData from "@/utils/fetchData";
import JourneySearches from "./JourneySearches.vue";
import { cents } from "@/utils/format";

const journeys = ref([]);
const showDepth = ref(null);
const searchItem = ref({});
const listPaginationRef = ref(null);

// One journey open at a time, shown under its row
const toggleDepth = (id) => {
  showDepth.value = showDepth.value === id ? null : id;
};

const updateList = async (page) => {
  const result = await fetchData("getJourneyList", {
    page,
    search: searchItem.value,
  });
  journeys.value = result.journeys;
  showDepth.value = null;
};

const search = async (search) => {
  searchItem.value = search;
  updateList(1);
  listPaginationRef.value.loadTotalPages(search);
};

const cancelSearch = () => {
  searchItem.value = {};
  updateList(1);
  listPaginationRef.value.loadTotalPages();
};

const RATINGS = {
  1: { icon: "thumbs-up", label: "Good", class: "text-up dark:text-up-dark" },
  "-1": {
    icon: "thumbs-down",
    label: "Bad",
    class: "text-down dark:text-down-dark",
  },
  0: {
    icon: "minus",
    label: "Neither",
    class: "text-secondary dark:text-night-muted",
  },
};

updateList(1);
</script>

<template>
  <section class="container mx-auto px-4 py-3">
    <div class="card p-5 sm:p-6">
      <h2 class="section-title">Journeys</h2>
      <p class="card-note mb-4">
        Every make, newest first. Select one to see each step.
      </p>
      <JourneySearches @search="search($event)" @cancel="cancelSearch()" />
      <div class="-mx-5 overflow-x-auto px-5 sm:-mx-6 sm:px-6">
        <table class="w-full min-w-[760px]">
          <thead>
            <tr>
              <th class="th">Prompt</th>
              <th class="th">Rating</th>
              <th class="th">User</th>
              <th class="th">Cost</th>
              <th class="th">Makes</th>
              <th class="th">When (UTC)</th>
              <th class="th w-8"><span class="sr-only">Open</span></th>
            </tr>
          </thead>
          <tbody>
            <template v-for="journey in journeys" :key="journey.id">
              <tr
                class="cursor-pointer transition-colors hover:bg-canvas dark:hover:bg-white/5"
                :aria-expanded="showDepth === journey.id"
                tabindex="0"
                @click="toggleDepth(journey.id)"
                @keydown.enter.prevent="toggleDepth(journey.id)"
              >
                <td class="td max-w-md font-medium">
                  <span class="line-clamp-2">{{ journey.prompt }}</span>
                </td>
                <td class="td">
                  <span
                    v-if="RATINGS[journey.vote]"
                    :class="RATINGS[journey.vote].class"
                    :title="RATINGS[journey.vote].label"
                  >
                    <font-awesome-icon :icon="RATINGS[journey.vote].icon" />
                    <span class="sr-only">{{
                      RATINGS[journey.vote].label
                    }}</span>
                  </span>
                  <span v-else class="text-secondary dark:text-night-muted"
                    >–</span
                  >
                </td>
                <td
                  class="td font-mono text-xs text-secondary dark:text-night-muted"
                >
                  {{ journey.user || "–" }}
                </td>
                <td class="td whitespace-nowrap">
                  {{ journey.cost ? cents(journey.cost) : "–" }}
                </td>
                <td class="td">{{ journey.generationCount }}</td>
                <td
                  class="td whitespace-nowrap text-secondary dark:text-night-muted"
                >
                  {{ journey.dateTime }}
                </td>
                <td class="td text-secondary dark:text-night-muted">
                  <font-awesome-icon
                    :icon="showDepth === journey.id ? 'angle-up' : 'angle-down'"
                  />
                </td>
              </tr>
              <tr v-if="showDepth === journey.id">
                <td colspan="7" class="border-b border-primary/5 py-4">
                  <JourneyDepth :id="journey.id" />
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
      <ListPagination
        ref="listPaginationRef"
        paginationUrl="getJourneyPagination"
        noun="journeys"
        @currentPageUpdate="updateList($event)"
      />
    </div>
  </section>
</template>
